import { asArray } from '../utils';
import { I18nText, translate } from '../i18n_text';
import { GridHostChoices } from '@/islands/props';
import * as namesApi from '../api/names';

/** How many matches a picker shows. One more is asked for, which is how "there are more" is known. */
export const CHOICE_PAGE_SIZE = 100;

/** As much of a filter condition as resolving its labels needs. */
interface FilterItem {
  field: string;
  value: unknown;
}

export interface GridChoice {
  value: string;
  label: string;
}

export interface GridChoicePage {
  choices: GridChoice[];
  hasMore: boolean;
}

/**
 * Where one filter's choices come from: matches for what was typed, and labels for ids handed over
 * without them (a filter restored from a link carries ids only). `seed` names the key of the grid's
 * own choices object those labels belong to.
 */
export interface ChoiceLookup<TSeed extends string = string> {
  search: (query: string) => Promise<GridChoice[]>;
  resolve: (ids: string[]) => Promise<GridChoice[]>;
  seed: TSeed;
}

export type NamedEntity = { id?: string; name: string } | { id?: string; name_json: I18nText };

export function toChoices(entities: NamedEntity[]): GridChoice[] {
  return entities.map((entity) => ({
    value: String(entity.id),
    label: 'name_json' in entity ? translate(entity.name_json) : entity.name,
  }));
}

/** One page of matches for a query, in the shape the entity list endpoints take. */
export function choicePageParams(query: string) {
  return {
    offset: 0,
    limit: CHOICE_PAGE_SIZE + 1,
    name_prefix: query.trim().toLocaleLowerCase(),
  };
}

export type ChoicePageParams = ReturnType<typeof choicePageParams>;

export function entityLookup<TSeed extends string>(
  search: (params: ChoicePageParams) => Promise<NamedEntity[]>,
  resolve: (ids: string[]) => Promise<NamedEntity[]>,
  seed: TSeed
): ChoiceLookup<TSeed> {
  return {
    search: (query) => search(choicePageParams(query)).then(toChoices),
    resolve: (ids) => resolve(ids).then(toChoices),
    seed,
  };
}

export function listLookup<TSeed extends string>(
  list: (params: ChoicePageParams | { ids: string[] }) => Promise<NamedEntity[]>,
  seed: TSeed
): ChoiceLookup<TSeed> {
  return entityLookup(list, (ids) => list({ ids }), seed);
}

export const globalRegionsLookup = listLookup(
  (params) => namesApi.list('global_regions', params),
  'globalRegion'
);

export type NoChoices = Record<string, never>;

/** The lookups behind each filter field, and the labels a picker shows without searching. */
export class GridChoices<
  TChoices extends Record<keyof TChoices, GridChoice[]>,
> implements GridHostChoices<TChoices> {
  /** What a picker can show before it searches: fixed vocabularies, and resolved selections. */
  seeded: TChoices;

  constructor(
    private lookups: { [field: string]: ChoiceLookup<Extract<keyof TChoices, string>> | undefined },
    private empty: () => TChoices
  ) {
    this.seeded = empty();
  }

  /**
   * Answers a picker: the first matches, and whether more exist. A field with no lookup has no
   * server-side vocabulary, which is not an error — the grid asks per column.
   */
  search = (field: string, query: string): Promise<GridChoicePage> => {
    const lookup = this.lookups[field];
    if (!lookup) {
      return Promise.resolve({ choices: [], hasMore: false });
    }
    return lookup.search(query).then((choices) => ({
      choices: choices.slice(0, CHOICE_PAGE_SIZE),
      hasMore: choices.length > CHOICE_PAGE_SIZE,
    }));
  };

  seed(rawFilter: string): Promise<void> {
    return this.resolveSelected(rawFilter).then((selected) => {
      this.seeded = selected;
    });
  }

  /**
   * Labels for the values a restored filter already selects, by id — one call per field the filter
   * names, so a first visit costs nothing. An unparseable filter yields no labels rather than
   * throwing: a grid ignores such a filter too. Neither does a field whose lookup fails, since the
   * tenant default can filter on a vocabulary the viewer's role is not allowed to read.
   */
  private resolveSelected = (rawFilter: string): Promise<TChoices> => {
    const selectedByField: { [field: string]: string[] | undefined } = {};
    try {
      const model = rawFilter ? JSON.parse(rawFilter) : null;
      for (const item of asArray<FilterItem>(model && model.items)) {
        const values = Array.isArray(item.value) ? item.value : [item.value];
        const ids = values.filter((value: unknown) => value !== '' && value != null).map(String);
        if (this.lookups[item.field] && ids.length > 0) {
          selectedByField[item.field] = (selectedByField[item.field] || []).concat(ids);
        }
      }
    } catch (error) {
      return Promise.resolve(this.empty());
    }

    const fields = Object.keys(selectedByField);
    return Promise.all(
      fields.map((field) =>
        this.lookups[field]!.resolve(selectedByField[field] || []).catch((): GridChoice[] => [])
      )
    ).then((resolved) => {
      // One cast, because a write through a generic key is not something TypeScript can check.
      const choices = this.empty() as Record<string, GridChoice[]>;
      fields.forEach((field, index) => {
        choices[this.lookups[field]!.seed] = resolved[index];
      });
      return choices as TChoices;
    });
  };
}
